export default prompt = {
    base: `
Generate a task that guides the user on how to fulfill their request.

Requirements :

2. Clarity & Accessibility
    - Make the steps detailed enough for everyone to understand it without prior knowledge

3. Output Format
    - Provide the response in JSON format with the following structure:
        {
            "title": "Short task title",
            "text": "General description of the task",
            "steps": [
                {
                    "ref": "Unique id for the step",
                    "title": "Step title",
                    "text": "Clear explanation of the step",
                    "type": 0 | 1 | 2 | 3,
                    // Additional fields based on type
                }
            ]
        }

4. Step Structure
    Each step must include :
        - title: Brief name of the step
        - text: Clear instruction for the step
        - type: Defines the step’s function
        - Optionally, goto: The ref of the next step to jump to (for branching logic)

5. Step Types and Fields
    The graph has a default start node containing the title and text and a default end node. Dont include these in your answer.
    - Type 0: Action - no additional fields
    - Type 1: Checklist - includes a check array with objects:
        "check": [
            {
                "title": "Item title",
                "text": "Item description"
            }
        ]
    - Type 2: Wait - includes :
        "time": Number of seconds to wait
    - Type 3: Condition - branching step that includes:
        "yes": Ref of step to jump forward to if condition is true (relative),
        "no": Ref of step to jump forward to if condition is false (relative)
        > goto is not allowed in this step.

6. Control Flow ( Optional goto )
    - Use goto to create if-then-else logic or loop back to earlier steps.
    - For example, after a conditional block, use goto to return to the main sequence.

7. Additional requirements:
`,
    language: {
        std: '- MANDATORY: The assistant must respond exclusively in the language which the user is speaking. Any output in another language is forbidden.',
        selected: '- MANDATORY: The assistant must respond exclusively in ${lang}. Non-${lang} output is forbidden.'
    },
    level: {
        easy: `- MANDATORY for text in the steps (these requirements should not influence which types of steps you use):
  1. Use short sentences (max ~12–15 words per sentence).
  2. Use common, basic vocabulary.
  3. Avoid jargon, technical terms, and abstractions.
  4. Prefer active voice.
  5. Explain one idea per sentence.
  6. Assume the reader may have cognitive or learning disabilities.
  7. Write at approximately CEFR A2 level.`,
        normal: `- Use everyday language`
    },
    steps: `- CRITICAL NUM hookup:

    The "steps" array MUST contain BETWEEN \${min} AND \${max} items (inclusive).

    This is a hard constraint.

    Before producing the final JSON:

    1. Count the number of steps.
    2. If the count is less than \${min}, add steps until it reaches the minimum.
    3. If the count is greater than \${max}, remove steps until it is within range.
    4. Recount.
    5. Only output once the count is valid.

    If the constraint is violated, regenerate silently until it is satisfied.`,
    types: `HARD CONSTRAINT — STEP.TYPE MUST BE NUMERIC ENUM

    The step "type" field is a NUMBER. Valid values are:
    0 = Action
    1 = Checklist
    2 = Wait
    3 = Condition

    ALLOWED_STEP_TYPES_NUMERIC = \${types}

    Rules:
    - Every step MUST have "type": \${types}.

    Hard failure rule:
    - If any step.type is not exactly \${types}, discard the entire output and regenerate from scratch.

    Output rule:
    - Output ONLY the final JSON object and nothing else.
    `
};
